import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import HubGroupsService from "../../../app/data/hub-groups/hubGroupsService";
import { HubGroupDto, HubGroupRequest } from "../../../app/data/hub-groups/models";
import { ApiResponse } from "../../../app/data/api";
import { HubDefinition } from "../outboundPlanningGridUtils";

const hubGroupsService = HubGroupsService.getInstance();
const LEGACY_HUB_STORAGE_PREFIX = "outboundPlanningHubs:v1:";

/** Remove per-origin hub definitions previously stored in localStorage. */
function clearLegacyHubLocalStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(LEGACY_HUB_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

function ensureOk<T>(res: ApiResponse<T>): T {
  if (typeof res?.isCancel === "function" && res.isCancel()) {
    const err = new Error("Aborted");
    err.name = "AbortError";
    throw err;
  }
  if (typeof res?.ok === "function" && !res.ok()) {
    const raw = typeof res.getError === "function" ? res.getError() : null;
    const message =
      typeof raw === "string"
        ? raw
        : (raw as { message?: string })?.message ?? "Request failed.";
    throw new Error(message);
  }
  return res.data;
}

export function toHubDefinition(dto: HubGroupDto): HubDefinition {
  return {
    id: dto.id,
    name: dto.name,
    terminalNumbers: [...(dto.terminalNumbers ?? [])],
  };
}

/** Other hubs that must drop terminals so a terminal stays in at most one hub. */
function exclusivityUpdates(
  hubs: HubGroupDto[],
  targetHubId: string | null,
  terminalNumbers: number[]
): { id: string; request: HubGroupRequest }[] {
  const assignSet = new Set(terminalNumbers);
  return hubs
    .filter((h) => h.id !== targetHubId)
    .filter((h) => h.terminalNumbers.some((t) => assignSet.has(t)))
    .map((h) => ({
      id: h.id,
      request: {
        name: h.name,
        terminalNumbers: h.terminalNumbers.filter((t) => !assignSet.has(t)),
      },
    }));
}

async function applyExclusivityUpdates(
  updates: { id: string; request: HubGroupRequest }[]
): Promise<void> {
  for (const update of updates) {
    ensureOk(await hubGroupsService.updateHubGroup(update.id, update.request));
  }
}

export function useHubGroups() {
  const queryClient = useQueryClient();
  const queryKey = [QUERY_KEYS.HUB_GROUPS] as const;

  useEffect(() => {
    clearLegacyHubLocalStorage();
  }, []);

  const listQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const data = ensureOk(await hubGroupsService.listHubGroups());
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60_000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...queryKey] });

  const createMutation = useMutation({
    mutationFn: async (request: HubGroupRequest) => {
      const hubs = listQuery.data ?? [];
      const created = ensureOk(await hubGroupsService.createHubGroup(request));
      await applyExclusivityUpdates(
        exclusivityUpdates(hubs, created.id, request.terminalNumbers ?? [])
      );
      return created;
    },
    onSuccess: () => {
      void invalidate();
      toast.info("Hub group created.");
    },
    onError: (e: Error) => {
      if (e.name === "AbortError") return;
      toast.error(e.message || "Failed to create hub group.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: string;
      request: HubGroupRequest;
    }) => {
      const hubs = listQuery.data ?? [];
      const updated = ensureOk(await hubGroupsService.updateHubGroup(id, request));
      await applyExclusivityUpdates(
        exclusivityUpdates(hubs, id, request.terminalNumbers ?? [])
      );
      return updated;
    },
    onSuccess: () => {
      void invalidate();
      toast.info("Hub group updated.");
    },
    onError: (e: Error) => {
      if (e.name === "AbortError") return;
      toast.error(e.message || "Failed to update hub group.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      ensureOk(await hubGroupsService.deleteHubGroup(id));
    },
    onSuccess: () => {
      void invalidate();
      toast.info("Hub group removed.");
    },
    onError: (e: Error) => {
      if (e.name === "AbortError") return;
      toast.error(e.message || "Failed to remove hub group.");
    },
  });

  const hubs: HubDefinition[] = (listQuery.data ?? []).map(toHubDefinition);

  const isMutating =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return {
    hubs,
    hubDtos: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    error: listQuery.error,
    isMutating,
    createHub: createMutation.mutateAsync,
    updateHub: updateMutation.mutateAsync,
    deleteHub: deleteMutation.mutateAsync,
  };
}
